import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Card, Typography, Tabs, Spin, Alert, Button } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';

import { avaliacoesAdminService } from '../../services/api';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import { buildApiErrorMessage } from '../../utils/apiError';
import PeriodoResumoPainel from './PeriodoResumoPainel';
import PeriodoTurmasPainel from './PeriodoTurmasPainel';
import PeriodoAcompanhamentoPainel from './PeriodoAcompanhamentoPainel';

const { Title, Text } = Typography;

// Tela de detalhe de UM período (Fase 7) -- Resumo/Turmas/Acompanhamento
// como abas da MESMA entidade, não rotas separadas (mesmo princípio de
// simplicidade da tela principal). `periodo` é buscado uma vez aqui e
// repassado às 3 abas; qualquer ação que muda o período (editar/abrir/
// encerrar, na aba Resumo) recarrega via `onAtualizado`, refletindo nas
// outras abas também (ex.: Turmas passa a permitir "Adicionar" assim que
// o status vira ABERTO, sem precisar trocar de aba).
const PeriodoAvaliacaoAdminDetalhe = () => {
  const { periodoId } = useParams();
  const navigate = useNavigate();
  const { loading: permissaoLoading, isAdmin, podeVisualizar } = usePermissaoRecurso('avaliacoes_gestao');

  const [periodo, setPeriodo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const response = await avaliacoesAdminService.obterPeriodo(periodoId);
      setPeriodo(response.data);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(err, 'Período ou turma não encontrado.');
      setErro(mensagem);
    } finally {
      setLoading(false);
    }
  }, [periodoId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  return (
    <Card>
      <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate('/avaliacoes')} style={{ marginBottom: 12 }}>
        Voltar
      </Button>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && erro && (
        <Alert
          type="error"
          showIcon
          message="Não foi possível abrir o período"
          description={erro}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {!loading && !erro && periodo && (
        <>
          <Title level={3} style={{ marginBottom: 4 }}>
            {periodo.nome}
          </Title>
          <Text type="secondary">Gestão deste período de avaliação.</Text>

          <Tabs
            style={{ marginTop: 24 }}
            defaultActiveKey="resumo"
            items={[
              {
                key: 'resumo',
                label: 'Resumo',
                children: <PeriodoResumoPainel periodo={periodo} onAtualizado={carregar} />,
              },
              {
                key: 'turmas',
                label: 'Turmas',
                children: <PeriodoTurmasPainel periodo={periodo} onTurmaIncluida={carregar} />,
              },
              {
                key: 'acompanhamento',
                label: 'Acompanhamento',
                children: <PeriodoAcompanhamentoPainel periodo={periodo} />,
              },
            ]}
          />
        </>
      )}
    </Card>
  );
};

export default PeriodoAvaliacaoAdminDetalhe;
